import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { GenerationResult } from '@gilding/shared';
import { STYLE_PROFILES } from '@gilding/shared';
import { LivingSculpt, frameOuterAspect, type CaptureClip, type SculptControls } from './LivingSculpt.js';
import { MotionClipSheet } from './MotionClipSheet.js';
import { cn } from '../lib/classnames.js';

// A just-made gild may still be having its depth + spatial bundle built (gen-time
// precompute / the cron sweep). For this long AFTER creation we treat a flat piece
// as "still preparing" — poll for its bundle and swap in the living sculpt the
// moment it lands. Past this window a still-flat piece is genuinely flat (or fal is
// down), so we stop, and never show a misleading "adding depth" indicator on it.
const SPATIAL_PREPARE_WINDOW_MS = 8 * 60 * 1000;
const SPATIAL_POLL_MS = 4000;

// Secondary control pill — same geometry as the molten-gold `.btn-primary`
// (rounded-full, px-7 py-3, ≥2.875rem tall) so primary + secondary pills line up
// in one row; a quiet frosted glass that warms to gold on hover.
const PILL_SECONDARY =
  'inline-flex min-h-[2.875rem] w-full select-none items-center justify-center gap-2 ' +
  'rounded-full border border-hairline-strong/55 bg-surface-elevated/15 px-7 py-3 ' +
  'font-display text-[0.9375rem] font-semibold text-ink-soft backdrop-blur-sm ' +
  'transition-[transform,background,border-color,color] duration-300 ' +
  'hover:border-accent/55 hover:bg-surface-elevated/25 hover:text-ink active:scale-[0.99] ' +
  'disabled:opacity-55 disabled:pointer-events-none';

/**
 * GalleryLightbox — a clean, full-bleed modal for a single gilding. The piece
 * appears large and materialises out of gilt (the same reveal as a fresh
 * generation); beneath it, an "Open prompt" button links through to the full
 * piece page. Backdrop click and Escape close it; body scroll is locked while
 * open. Portaled to <body> so nothing clips it.
 *
 * Owner actions (publish/unpublish + delete) can be passed in — they render as a
 * roomy, reliable control row here. On the gallery wall the per-tile ellipsis is
 * a tiny, fisheye-magnified moving target that's awkward to hit on desktop; the
 * lightbox (opened by a click, with the wall frozen) is the dependable place to
 * manage a piece on every device.
 */
export function GalleryLightbox({
  gen,
  onClose,
  onPublish,
  onUnpublish,
  onDelete,
  onChargeHdClip,
  mutating = false,
}: {
  gen: GenerationResult;
  onClose: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
  onDelete?: () => void;
  /** Deduct one leaf for an HD clip server-side (resolves ok=false on failure). */
  onChargeHdClip?: () => Promise<{ ok: boolean; error?: string }>;
  mutating?: boolean;
}) {
  const navigate = useNavigate();
  const canManage = Boolean(onPublish || onUnpublish || onDelete);
  // Optimistic feed state so the toggle's label flips instantly (the parent's
  // mutation + query invalidation reconcile the source of truth behind it).
  const [localPublic, setLocalPublic] = useState(gen.visibility === 'public');
  const [confirming, setConfirming] = useState(false);
  // Motion-clip export: the live scene hands us its orbit-capture fn when ready.
  const captureRef = useRef<CaptureClip | null>(null);
  const [hasCapture, setHasCapture] = useState(false);
  const [clipOpen, setClipOpen] = useState(false);
  // Spatial readiness — true when the validated spatial2 layer set loaded (true
  // layered parallax). False = the premium FLAT presentation: still exportable
  // (the whole-card tilt orbit), so the Animate button stays enabled either way;
  // we just tell the user which motion they'll get.
  const [spatialReady, setSpatialReady] = useState(false);
  const canClip = Boolean(onChargeHdClip);
  // Stable so it doesn't re-run the sculpt's build effect each render.
  const handleCaptureReady = useCallback((capture: CaptureClip | null) => {
    captureRef.current = capture;
    setHasCapture(Boolean(capture));
  }, []);
  const handleSpatialReady = useCallback((ready: boolean) => {
    setSpatialReady(ready);
  }, []);

  // The piece's spatial2 bundle URL — shared by the sculpt prop and the poll below.
  const spatialUrl = `https://api.gilding.app/images/spatial2/${gen.id}-${gen.coverIndex ?? 0}`;

  // AUTO-UPGRADE: while a recent piece is still flat, poll for its bundle and remount
  // the sculpt (which re-loads it spatial) the instant the manifest appears — no
  // refresh. `preparing` drives the subtle "adding depth…" hint; it's only ever true
  // for RECENT pieces, so an older/genuinely-flat piece never shows it.
  const [spatialKey, setSpatialKey] = useState(0);
  const [preparing, setPreparing] = useState(false);
  useEffect(() => {
    setPreparing(false);
    if (spatialReady) return undefined; // already a living sculpt
    const deadline = new Date(gen.createdAt).getTime() + SPATIAL_PREPARE_WINDOW_MS;
    if (Date.now() >= deadline) return undefined; // too old → treat as genuinely flat
    setPreparing(true);
    let stopped = false;
    let timer = 0;
    const tick = async (): Promise<void> => {
      if (stopped) return;
      if (Date.now() >= deadline) {
        setPreparing(false); // gave the depth sweep enough time; it's flat for now
        return;
      }
      try {
        // meta.json is written LAST when a bundle completes, so a 200 here means the
        // whole set (solo.png + solodepth.png) is already in place — safe to remount.
        const r = await fetch(`${spatialUrl}/meta.json`, { cache: 'no-store' });
        if (!stopped && r.ok) {
          setSpatialKey((k) => k + 1); // manifest landed → remount re-loads it spatial
          return;
        }
      } catch {
        /* transient — retry */
      }
      timer = window.setTimeout(() => void tick(), SPATIAL_POLL_MS);
    };
    timer = window.setTimeout(() => void tick(), SPATIAL_POLL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [spatialReady, spatialUrl, gen.createdAt]);

  // Live-sculpt controls (tilt + auto-play), surfaced as icon buttons ABOVE the
  // frame. `canTilt` is true only where a phone gyro needs an explicit unlock.
  const controlsRef = useRef<SculptControls | null>(null);
  const [hasControls, setHasControls] = useState(false);
  const [canTilt, setCanTilt] = useState(false);
  // Default the bold auto-orbit ON so an opened gilding MOVES immediately — the
  // same motion as the marketing/preview sculpts (which are ambient by default).
  // Was false (pieces sat still until you tapped play or tilted), which read as
  // "the live site isn't animating like the previews" (user 2026-07-05). Tilting
  // still takes over (onTilt → setAmbient(false) + enableMotion).
  const [autoPlaying, setAutoPlaying] = useState(true);
  // Stable mirror of autoPlaying so handleControlsReady can read the CURRENT value
  // without changing identity (it's in LivingSculpt's effect deps — a new identity
  // would rebuild the scene). Lets us re-sync ambient on the auto-upgrade remount.
  const autoPlayingRef = useRef(autoPlaying);
  useEffect(() => {
    autoPlayingRef.current = autoPlaying;
  }, [autoPlaying]);
  const handleControlsReady = useCallback(
    (c: (SculptControls & { canTilt: boolean }) | null) => {
      controlsRef.current = c;
      setHasControls(Boolean(c));
      setCanTilt(Boolean(c?.canTilt));
      // Match the sculpt's motion to the play/pause button on every (re)mount —
      // including the flat→spatial auto-upgrade, so a paused piece stays paused.
      if (c) c.setAmbient(autoPlayingRef.current);
    },
    [],
  );
  // Each opened/navigated gilding starts auto-playing. The sculpt itself starts on
  // the ambient orbit (stable `ambient` prop below); this just re-syncs the button
  // state so it reads "playing" for the fresh piece.
  useEffect(() => {
    setAutoPlaying(true);
    setSpatialReady(false); // new piece: its own load re-decides spatial vs flat
  }, [gen.id]);
  const onTilt = (): void => {
    // Tilting takes over from auto-play; unlock the gyro (iOS permission prompt).
    if (autoPlaying) {
      controlsRef.current?.setAmbient(false);
      setAutoPlaying(false);
    }
    controlsRef.current?.enableMotion();
  };
  const onToggleAutoPlay = (): void => {
    const next = !autoPlaying;
    controlsRef.current?.setAmbient(next);
    setAutoPlaying(next);
  };

  const image = gen.images[gen.coverIndex ?? 0]?.url ?? gen.images[0]?.url;
  const styleLabel = STYLE_PROFILES[gen.request.stylePreset]?.label ?? gen.request.stylePreset;
  // Aspect (w/h) of this gilding — picks the matching frame + canvas shape.
  const ASPECTS: Record<string, number> = {
    '1:1': 1,
    '3:2': 3 / 2,
    '2:3': 2 / 3,
    '16:9': 16 / 9,
    '9:16': 9 / 16,
  };
  const aspect = ASPECTS[gen.request.aspectRatio] ?? 2 / 3;

  // Escape to close + lock background scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="backdrop-enter fixed inset-0 z-50 overflow-y-auto overflow-x-hidden overscroll-contain"
      style={{ background: 'rgb(6 5 8 / 0.86)', backdropFilter: 'blur(10px)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Gilding preview"
      onClick={onClose}
    >
      {/* Close — a quiet glass disc, always reachable. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-ink/45 text-white/85 backdrop-blur-[3px] transition-colors duration-200 hover:bg-ink/70 hover:text-white"
        style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
          <path d="M5 5l10 10M15 5L5 15" />
        </svg>
      </button>

      {/* Full-width centered column. NOT max-w-3xl: a landscape frame is wider than
          that cap and would overflow it, drifting off-centre — the centering
          reference must be the whole viewport so every aspect sits dead-centre. */}
      <div
        className="sheet-enter mx-auto flex min-h-dvh w-full flex-col items-center justify-center gap-4 px-4 py-6 sm:gap-6 sm:py-12"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Live-motion controls — small icon buttons ABOVE the frame: unlock
            phone-tilt (gyro), and auto-play the same orbit as the download. */}
        {hasControls && (
          <div className="flex items-center justify-center gap-3">
            {canTilt && (
              <SculptIconButton onClick={onTilt} label="Tilt with your phone">
                <TiltGlyph />
              </SculptIconButton>
            )}
            <SculptIconButton
              onClick={onToggleAutoPlay}
              active={autoPlaying}
              label={autoPlaying ? 'Stop auto-play' : 'Auto-play the motion'}
            >
              {autoPlaying ? <PauseGlyph /> : <PlayGlyph />}
            </SculptIconButton>
          </div>
        )}

        {/* The piece as a living 3D sculpt inside a carved gilded frame — tilt
            the phone (gyroscope) or drag to relight it. Falls back to the plain
            framed image on reduced-motion / no-WebGL. */}
        <figure className="relative mx-auto flex items-center justify-center">
          {image ? (
            <LivingSculpt
              // `spatialKey` bumps when the bundle lands mid-view → remount re-loads
              // the piece as a living sculpt in place (the auto-upgrade).
              key={`${gen.id}::${spatialKey}`}
              src={image}
              spatialUrl={spatialUrl}
              aspect={aspect}
              // Start on the bold auto-orbit (matches the preview/marketing sculpts).
              // MUST be a STABLE value, NOT {autoPlaying}: `ambient` is in LivingSculpt's
              // setup-effect deps, so a changing prop tears down + rebuilds the whole scene
              // on every pause/play tap (froze + disappeared — user 2026-07-05). Runtime
              // pause/play is driven imperatively via controlsRef.setAmbient instead.
              ambient
              alt={`${styleLabel} gilding`}
              // The lightbox renders its own tilt/auto-play controls above the
              // frame, so suppress the in-canvas "tap to tilt" overlay.
              motionHint={false}
              // Cap the piece's height so it + the controls (a row above, the
              // dock below) fit one screen (mobile) without pushing buttons off.
              heightCap={canClip ? 'min(74svh, calc(100svh - 21rem))' : 'min(78svh, calc(100svh - 18rem))'}
              onCaptureReady={handleCaptureReady}
              onControlsReady={handleControlsReady}
              onSpatialReady={handleSpatialReady}
            />
          ) : null}
          {/* Auto-upgrade hint — a subtle gold pill while the depth + spatial layer
              finishes building for a just-made piece. Fades out the instant the
              living sculpt takes over (spatialReady). Never shown on older/flat pieces. */}
          {preparing && (
            <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/35 bg-black/55 px-3.5 py-1.5 font-display text-caption italic text-[#f0d79a] shadow-lift backdrop-blur-sm">
                <span className="relative flex h-2 w-2" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent/70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                </span>
                Adding depth…
              </span>
            </div>
          )}
        </figure>

        {/* Control cluster — one aligned, premium dock under the piece. The
            primary actions sit as equal-width pills; delete is set apart and
            quieted so it's reachable but never an easy mis-tap. */}
        <div className="flex w-full max-w-sm flex-col items-center gap-4">
          {/* Primary actions: full-width pills stacked in a tidy column — big,
              evenly aligned targets that read at a glance on every screen. */}
          <div className="flex w-full flex-col gap-2.5">
            {/* The hero action — turn the gilding into a shareable orbit clip.
                Molten-gold so it draws the eye; opens the export studio. */}
            {canClip && (
              <>
                <button
                  type="button"
                  onClick={() => setClipOpen(true)}
                  disabled={!hasCapture}
                  className="btn-primary btn-cta w-full"
                >
                  <AnimateDownloadGlyph size={18} />
                  Animate &amp; download
                </button>
                {hasCapture && !spatialReady && (
                  <p className="text-center font-display text-micro italic text-ink-subtle">
                    Spatial layers still preparing — this one exports with the framed-tilt motion.
                  </p>
                )}
              </>
            )}

            {/* Publish toggle — a quiet outline pill (the gold is reserved for the
                clip CTA above so there's a single clear hero). */}
            {canManage && (onPublish || onUnpublish) && (
              <button
                type="button"
                disabled={mutating}
                onClick={() => {
                  if (localPublic) {
                    setLocalPublic(false);
                    onUnpublish?.();
                  } else {
                    setLocalPublic(true);
                    onPublish?.();
                  }
                }}
                className={PILL_SECONDARY}
              >
                {localPublic ? <LockGlyph /> : <EyeGlyph />}
                {mutating ? (localPublic ? 'Updating…' : 'Publishing…') : localPublic ? 'Take off the feed' : 'Publish to the feed'}
              </button>
            )}

            {/* Open prompt — the full piece page. */}
            <button
              type="button"
              onClick={() => navigate(`/result/${gen.id}`, { state: gen })}
              className={cn(PILL_SECONDARY, 'group')}
            >
              <GlyphQuill />
              Open prompt
              <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-0.5">
                →
              </span>
            </button>
          </div>

          {/* Delete — separated by a hairline and de-emphasized. Expands inline
              into a guarded confirm so a stray tap can't erase a piece. */}
          {onDelete && (
            <>
              <span aria-hidden className="h-px w-12 bg-hairline/50" />
              {confirming ? (
                <div className="flex w-full flex-col items-center gap-3 rounded-2xl border border-danger/25 bg-surface-elevated/85 px-5 py-4 text-center backdrop-blur-md shadow-lift">
                  <p className="font-display text-caption font-medium text-ink">
                    Delete this gilding permanently?
                  </p>
                  <p className="font-display text-micro italic text-ink-subtle">
                    Removes it from your gallery{localPublic ? ' and the feed' : ''}. This can’t be undone.
                  </p>
                  <div className="mt-1 flex w-full gap-2.5">
                    <button
                      type="button"
                      disabled={mutating}
                      onClick={() => {
                        onDelete();
                        onClose();
                      }}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-danger px-5 py-2.5 font-body text-micro font-semibold text-surface transition-[transform,filter] duration-150 hover:brightness-110 active:scale-[0.98] disabled:opacity-55"
                    >
                      <TrashGlyph />
                      {mutating ? 'Deleting…' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      className="flex-1 rounded-full border border-hairline-strong/55 px-5 py-2.5 font-body text-micro font-medium text-ink-soft transition-colors duration-150 hover:bg-surface-sunken hover:text-ink"
                    >
                      Keep
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-body text-micro text-ink-subtle transition-colors duration-200 hover:bg-danger/10 hover:text-danger"
                >
                  <TrashGlyph />
                  Delete from gallery
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {canClip && (
        <MotionClipSheet
          open={clipOpen}
          onClose={() => setClipOpen(false)}
          capture={hasCapture ? captureRef.current : null}
          frameAspect={frameOuterAspect(aspect)}
          slug={gen.id.replace(/^gen_/, '').slice(0, 8)}
          generationId={gen.id}
          imageIndex={gen.coverIndex ?? 0}
          onChargeHd={async () => (onChargeHdClip ? onChargeHdClip() : { ok: false })}
        />
      )}
    </div>,
    document.body,
  );
}

function EyeGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-accent-deep">
      <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="1.8" />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="7" width="9" height="6.5" rx="1" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </svg>
  );
}

function TrashGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6h12" />
      <path d="M8 6V4.5A1 1 0 0 1 9 3.5h2a1 1 0 0 1 1 1V6" />
      <path d="M5.5 6l.7 9a1.5 1.5 0 0 0 1.5 1.4h4.6a1.5 1.5 0 0 0 1.5-1.4l.7-9" />
      <path d="M8.5 9.5v4M11.5 9.5v4" />
    </svg>
  );
}

/** A round glass icon button — the tilt / auto-play affordances above the frame.
 *  Quiet by default, warming to gold; the active (playing) state holds the gold. */
function SculptIconButton({
  onClick,
  label,
  active = false,
  children,
}: {
  onClick: () => void;
  label: string;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn(
        'inline-flex h-11 w-11 items-center justify-center rounded-full border backdrop-blur-sm',
        'transition-[transform,background,border-color,color] duration-300 active:scale-95',
        active
          ? 'border-accent/70 bg-accent/15 text-accent-deep shadow-[0_0_0_1px_rgba(233,200,115,0.25)]'
          : 'border-hairline-strong/55 bg-surface-elevated/15 text-ink-soft hover:border-accent/55 hover:bg-surface-elevated/25 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** Phone-tilt: a slightly-rotated handset with a rotation arc — "tilt to relight". */
function TiltGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <g transform="rotate(-15 12 12)">
        <rect x="8.5" y="3.5" width="7" height="17" rx="1.8" />
        <line x1="10.7" y1="17.8" x2="13.3" y2="17.8" />
      </g>
      <path d="M4.2 9.5 A 8.4 8.4 0 0 0 4.2 14.5" />
      <path d="M19.8 9.5 A 8.4 8.4 0 0 1 19.8 14.5" />
    </svg>
  );
}

/** Play triangle — start the auto-orbit. */
function PlayGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.2 L18.2 12 L8 18.8 Z" />
    </svg>
  );
}

/** Pause — stop the auto-orbit. */
function PauseGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6.5" y="5" width="3.6" height="14" rx="1.1" />
      <rect x="13.9" y="5" width="3.6" height="14" rx="1.1" />
    </svg>
  );
}

/** Animate & download — a framed clip (rounded frame + play) with a download
 *  arrow dropping out of it. Replaces the old orbit-satellite mark. */
function AnimateDownloadGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* the clip frame */}
      <rect x="3.75" y="3" width="16.5" height="11.5" rx="2.3" />
      {/* play triangle inside */}
      <path d="M10 6.2 L14.4 8.75 L10 11.3 Z" fill="currentColor" stroke="none" />
      {/* download arrow dropping out the bottom */}
      <path d="M12 14.6 V21" />
      <path d="M9.3 18.3 L12 21 L14.7 18.3" />
    </svg>
  );
}

function GlyphQuill() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-accent-deep"
    >
      <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" />
      <path d="M16 8 2 22" />
    </svg>
  );
}
